import { useEffect, useState } from "react";
import { Button, Checkbox, Segmented, Slider, Typography } from "antd";
import {
  AimOutlined,
  BorderOutlined,
  DeleteOutlined,
  HighlightOutlined,
  ScissorOutlined,
  SelectOutlined,
} from "@ant-design/icons";
import type { DraftAction } from "../../masterImages/draft";
import type { BrickRegion, BrickType } from "../../masterImages/types";
import { HALF_TINT, REGION_COLOURS, isBrush, type Tool } from "./EditorCanvas";
import { CONTOUR, CUT_AWAY } from "./useOutlines";

interface Props {
  tool: Tool;
  onTool(tool: Tool): void;
  brushRadius: number;
  onBrushRadius(radius: number): void;
  tolerance: number;
  selected: BrickRegion[];
  dispatch(action: DraftAction): void;
}

const Swatch: React.FC<{ type: BrickType }> = ({ type }) => (
  <span
    style={{
      display: "inline-block",
      width: 22,
      height: 14,
      marginRight: 6,
      verticalAlign: "middle",
      border: `2px ${type === "half" ? "dashed" : "solid"} ${REGION_COLOURS[type]}`,
      background: type === "half" ? HALF_TINT : "transparent",
    }}
  />
);

const BRUSH_HINTS = {
  brushAdd: "Paint over parts that belong to the brick. The outline updates as you paint.",
  brushErase: "Paint over joint that should be cut away. The outline updates as you paint.",
  brushSnap: "Paint along a brick edge: under the brush, joint is cut and brick is kept, so the outline follows the edge.",
};

const ToolLabel: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <div style={{ padding: "4px 0", lineHeight: 1.3 }}>
    <div style={{ fontSize: 16 }}>{icon}</div>
    <div>{text}</div>
  </div>
);

const Key: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography.Text keyboard>{children}</Typography.Text>
);

const SHORTCUTS: [React.ReactNode, string][] = [
  [<><Key>←</Key><Key>→</Key><Key>↑</Key><Key>↓</Key></>, "Nudge 1 px"],
  [<><Key>Shift</Key>+ arrows</>, "Nudge 10 px"],
  [<Key>F</Key>, "Flip full / half"],
  [<Key>Delete</Key>, "Delete selection"],
  [<Key>Esc</Key>, "Clear selection"],
  [<><Key>⌘</Key>/<Key>Ctrl</Key>+<Key>Z</Key></>, "Undo"],
  [<><Key>⌘</Key>/<Key>Ctrl</Key>+<Key>Shift</Key>+<Key>Z</Key></>, "Redo"],
  [<><Key>Shift</Key>+ click</>, "Add to selection"],
  [<><Key>Space</Key>+ drag</>, "Pan (or drag on empty space)"],
  ["Scroll", "Zoom"],
];

const Toolbar: React.FC<Props> = ({
  tool,
  onTool,
  brushRadius,
  onBrushRadius,
  tolerance,
  selected,
  dispatch,
}) => {
  const [globalTolerance, setGlobalTolerance] = useState(tolerance);
  useEffect(() => setGlobalTolerance(tolerance), [tolerance]);
  const override = selected.length > 0 && selected.every((r) => r.tolerance != null);
  const [regionTolerance, setRegionTolerance] = useState(tolerance);
  useEffect(() => setRegionTolerance(selected[0]?.tolerance ?? tolerance), [selected, tolerance]);
  const selectedType = selected.every((r) => r.type === selected[0]?.type) ? selected[0]?.type : undefined;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Segmented
        block
        value={tool}
        onChange={(value) => onTool(value as Tool)}
        options={[
          { value: "select", label: <ToolLabel icon={<SelectOutlined />} text="Select" />, title: "Select, move and resize regions" },
          { value: "draw", label: <ToolLabel icon={<BorderOutlined />} text="Draw" />, title: "Draw a new region" },
          { value: "brushAdd", label: <ToolLabel icon={<HighlightOutlined />} text="Add" />, title: "Brush: paint brick into the outline" },
          { value: "brushErase", label: <ToolLabel icon={<ScissorOutlined />} text="Erase" />, title: "Brush: cut joint out of the outline" },
          { value: "brushSnap", label: <ToolLabel icon={<AimOutlined />} text="Snap" />, title: "Brush: snap the outline to the brick edge" },
        ]}
      />
      <div style={{ display: "flex", gap: 16 }}>
        <Typography.Text>
          <Swatch type="full" />
          Full brick
        </Typography.Text>
        <Typography.Text>
          <Swatch type="half" />
          Half brick
        </Typography.Text>
      </div>
      <Typography.Text>
        <span
          style={{
            display: "inline-block",
            width: 22,
            height: 14,
            marginRight: 6,
            verticalAlign: "middle",
            background: CUT_AWAY,
            outline: `1px solid ${CONTOUR}`,
            outlineOffset: -4,
          }}
        />
        Dimmed is cut away, the white line is the brick outline
      </Typography.Text>
      {isBrush(tool) && (
        <>
          <Typography.Text type="secondary">{BRUSH_HINTS[tool]}</Typography.Text>
          <Typography.Text>Brush size</Typography.Text>
          <Slider min={2} max={60} value={brushRadius} onChange={onBrushRadius} />
        </>
      )}
      <Typography.Text strong>Joint tolerance</Typography.Text>
      <Slider
        min={0}
        max={100}
        value={globalTolerance}
        onChange={setGlobalTolerance}
        onChangeComplete={(value) => dispatch({ type: "setTolerance", tolerance: value })}
      />
      {selected.length > 0 && (
        <>
          <Typography.Text strong>
            {selected.length} region{selected.length === 1 ? "" : "s"} selected
          </Typography.Text>
          <div style={{ display: "flex", gap: 8 }}>
            <Segmented
              style={{ flex: 1 }}
              block
              value={selectedType ?? ""}
              onChange={(value) => dispatch({ type: "setType", brickType: value as BrickType })}
              options={[
                { label: "Full", value: "full" },
                { label: "Half", value: "half" },
              ]}
            />
            <Button danger icon={<DeleteOutlined />} onClick={() => dispatch({ type: "delete" })}>
              Delete
            </Button>
          </div>
          {selected.some((r) => r.strokes.length > 0) && (
            <Button onClick={() => dispatch({ type: "clearStrokes" })}>Clear brush strokes</Button>
          )}
          {selectedType === undefined && (
            <Typography.Text type="secondary">The selection mixes full and half bricks.</Typography.Text>
          )}
          <Checkbox
            checked={override}
            onChange={(e) =>
              dispatch({ type: "setRegionTolerance", tolerance: e.target.checked ? regionTolerance : null })
            }
          >
            Own tolerance
          </Checkbox>
          {override && (
            <Slider
              min={0}
              max={100}
              value={regionTolerance}
              onChange={setRegionTolerance}
              onChangeComplete={(value) => dispatch({ type: "setRegionTolerance", tolerance: value })}
            />
          )}
        </>
      )}
      <Typography.Text strong>Shortcuts</Typography.Text>
      <ul style={{ margin: 0, paddingLeft: 0, listStyle: "none", display: "grid", gap: 4 }}>
        {SHORTCUTS.map(([keys, action]) => (
          <li key={action} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Typography.Text type="secondary">{action}</Typography.Text>
            <span style={{ whiteSpace: "nowrap" }}>{keys}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Toolbar;
